document.addEventListener('DOMContentLoaded', () => {
  const review = document.querySelector('.jdgm-all-reviews-text__text');
  if(review) {
    const rating = review.dataset.score;
    const totalReviews = review.dataset.numberOfReviews;
    if(rating && totalReviews) {
      localStorage.setItem('review-rating', JSON.stringify({
        rating: rating,
        totalReviews: totalReviews
      }));
    }
  }
});

class ItemScroller extends HTMLElement {
  constructor() {
    super();
    this.container = this.querySelector("[data-drag-container]");
    this.dragParent = this.querySelector("[data-drag-parent]");
    this.scrollBar = this.querySelector("[data-scrollbar]");

    if (!this.container || !this.dragParent) return;

    this.initDragToScroll();
    if (this.scrollBar) {
      this.initScrollbar();
    }

    this.container.setAttribute(
      "style",
      "--width: " + this.container.clientWidth + "px;"
    );

    window.addEventListener("resize", () => {
      this.container.setAttribute(
        "style",
        "--width: " + this.container.clientWidth + "px;"
      );
    });

    this._checkScrollable();
    this._resizeObserver = new ResizeObserver(() => {
      this._checkScrollable();
    });
    this._resizeObserver.observe(this.dragParent);
  }

  _checkScrollable() {
    const isScrollable = this.dragParent.scrollWidth > this.dragParent.clientWidth + 1;
    this.dragParent.classList.toggle('is-scrollable', isScrollable);
  }

  initDragToScroll() {
    const isOverflowing = (wrapper) => {
      return wrapper.clientWidth < wrapper.scrollWidth;
    };
    let containerOverflowing = isOverflowing(this.container);

    if (containerOverflowing) {
      this.dragToScroll(this.container);
      return;
    }
    this.dragToScroll(this.dragParent);
  }

  dragToScroll(slider) {
    let mouseDown = false;
    let start;
    let scrollLeft;
    let inactiveTimeout;

    slider.addEventListener("mousedown", (e) => {
      const target = e.target;

      mouseDown = true;
      start = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider.addEventListener("mouseup", () => {
      mouseDown = false;

      clearTimeout(inactiveTimeout);
      inactiveTimeout = setTimeout(() => {
        slider.classList.remove("active");
      }, 150);
    });

    slider.addEventListener("mousemove", (e) => {
      if (!mouseDown) return;
      e.preventDefault();

      if (!slider.classList.contains("active")) {
        slider.classList.add("active");
      }

      const x = e.pageX - slider.offsetLeft;
      const walk = (x - start) * 1;
      slider.scrollLeft = scrollLeft - walk;
    });

    slider.addEventListener("mouseleave", () => {
      mouseDown = false;

      clearTimeout(inactiveTimeout);
      inactiveTimeout = setTimeout(() => {
        slider.classList.remove("active");
      }, 150);
    });
  }

  initScrollbar() {
    const slider = this.dragParent;

    const track = this.scrollBar.parentElement;
    const thumb = this.scrollBar;

    const update = () => {
      const maxScroll = slider.scrollWidth - slider.clientWidth;
      
      if (maxScroll <= 0) {
        thumb.style.width = "100%";
        thumb.style.transform = "translateX(0)";
        track.classList.add("hidden");
        return;
      }

      track.classList.remove("hidden");

      const thumbWidth =
        (slider.clientWidth / slider.scrollWidth) * track.clientWidth;

      thumb.style.width = `${thumbWidth}px`;

      const progress = slider.scrollLeft / maxScroll;

      const maxMove = track.clientWidth - thumbWidth;

      thumb.style.transform = `translateX(${progress * maxMove}px)`;
    };

    slider.addEventListener("scroll", update, { passive: true });

    window.addEventListener("resize", update);

    update();
  }
}

if (!customElements.get("item-scroller")) {
  customElements.define("item-scroller", ItemScroller);
}

class CustomSelect extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector(".custom-select-trigger");
    this.options = this.querySelectorAll(".custom-option");
    this.hiddenSelect = this.querySelector("select");
    this.selectedText = this.querySelector(".selected-option");

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);

    this.trigger?.addEventListener("click", this.handleTriggerClick);

    this.options.forEach((option) => {
      option.addEventListener("click", this.handleOptionClick);
    });

    document.addEventListener("click", this.handleDocumentClick);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener("click", this.handleTriggerClick);

    this.options.forEach((option) => {
      option.removeEventListener("click", this.handleOptionClick);
    });

    document.removeEventListener("click", this.handleDocumentClick);
  }

  handleTriggerClick(event) {
    event.stopPropagation();

    document.querySelectorAll("custom-select.open").forEach((select) => {
      if (select !== this) select.classList.remove("open");
    });

    this.classList.toggle("open");
  }

  handleOptionClick(event) {
    event.stopPropagation();

    const option = event.currentTarget;
    const value = option.dataset.value;
    const text = option.textContent.trim();

    if (!this.hiddenSelect || !this.selectedText) return;

    if (this.hiddenSelect.value === value) {
      this.classList.remove("open");
      return;
    }

    this.selectedText.textContent = text;
    this.hiddenSelect.value = value;

    this.options.forEach((item) => {
      item.classList.remove("selected");
    });

    option.classList.add("selected");
    this.classList.remove("open");

    this.hiddenSelect.dispatchEvent(
      new Event("change", { bubbles: true })
    );
  }

  handleDocumentClick() {
    this.classList.remove("open");
  }
}

if (!customElements.get("custom-select")) {
  customElements.define("custom-select", CustomSelect);
}

class CoverflowSlider extends HTMLElement {
  constructor() {
    super();

    this.activeIndex = 0;
    this.slides = [];
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.dragThreshold = 50;
  }

  connectedCallback() {
    this.track = this.querySelector(".coverflow__track");
    this.slides = [...this.querySelectorAll(".coverflow__slide")];

    this.loop = this.getAttribute("loop") !== "false";
    this.enableSwipeOnMobile = this.getAttribute("swipe") !== "false";

    this.prevButton = this.querySelector(".coverflow__button--prev");
    this.nextButton = this.querySelector(".coverflow__button--next");

    if (!this.slides.length) return;

    this.bindEvents();
    this.update();
  }

  bindEvents() {
    /* =========================
       Navigation
    ========================= */
    this.prevButton?.addEventListener("click", () => {
      this.prev();
    });

    this.nextButton?.addEventListener("click", () => {
      this.next();
    });

    /* =========================
       Click slide
    ========================= */
    this.slides.forEach((slide, index) => {
      slide.addEventListener("click", () => {
        this.goTo(index);
      });
    });

    /* =========================
       Keyboard
    ========================= */
    this.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") {
        this.prev();
      }

      if (event.key === "ArrowRight") {
        this.next();
      }
    });

    this.setAttribute("tabindex", "0");

    /* =========================
       Touch + Mouse
    ========================= */

    if (this.enableSwipeOnMobile) {
      this.addEventListener("pointerdown", this.onPointerDown.bind(this));
      this.addEventListener("pointermove", this.onPointerMove.bind(this));
      this.addEventListener("pointerup", this.onPointerUp.bind(this));
      this.addEventListener("pointercancel", this.onPointerUp.bind(this));
      this.addEventListener("pointerleave", this.onPointerLeave.bind(this));
  
      /*
       * Cho phép touch swipe ngang.
       * Browser vẫn có thể scroll page theo chiều dọc.
       */
      this.style.touchAction = "pan-y";
    }
  }

  /* =========================
     Pointer / Swipe
  ========================= */

  onPointerDown(event) {
    // Chỉ xử lý chuột trái
    if (event.pointerType !== "touch") return;

    this.isDragging = true;
    this.hasDragged = false;

    this.startX = event.clientX;
    this.currentX = event.clientX;

    this.setPointerCapture?.(event.pointerId);

    this.classList.add("is-dragging");
  }

   onPointerMove(event) {
    if (!this.isDragging) return;

    this.currentX = event.clientX;

    const deltaX = this.currentX - this.startX;

    if (Math.abs(deltaX) > 10) {
      this.hasDragged = true;
    }
  }

  onPointerUp(event) {
    if (!this.isDragging) return;

    const deltaX = this.currentX - this.startX;

    this.isDragging = false;

    this.releasePointerCapture?.(event.pointerId);

    this.classList.remove("is-dragging");

    /*
     * Không đủ khoảng cách => không làm gì
     */
    if (Math.abs(deltaX) < this.dragThreshold) {
      return;
    }

    /*
     * Kéo sang trái => slide tiếp theo
     */
    if (deltaX < 0) {
      this.next();
    }

    /*
     * Kéo sang phải => slide trước
     */
    else {
      this.prev();
    }

    /*
     * Reset sau một frame để click event
     * không trigger goTo()
     */
    requestAnimationFrame(() => {
      this.hasDragged = false;
    });
  }

  onPointerLeave(event) {
    /*
     * Với mouse, nếu đang drag và rời khỏi element
     * thì vẫn xử lý gesture.
     */
    if (
      this.isDragging &&
      event.pointerType === "mouse"
    ) {
      this.onPointerUp(event);
    }
  }

  prev() {
    if (this.loop) {
      this.activeIndex =
        (this.activeIndex - 1 + this.slides.length) %
        this.slides.length;
    } else {
      this.activeIndex = Math.max(0, this.activeIndex - 1);
      this.prevButton?.setAttribute("disabled", "");
      this.nextButton?.removeAttribute("disabled");
    }

    this.update();
  }

  next() {
    if (this.loop) {
      this.activeIndex =
        (this.activeIndex + 1) %
        this.slides.length;
    } else {
      this.activeIndex = Math.min(
        this.slides.length - 1,
        this.activeIndex + 1
      );
      this.prevButton?.removeAttribute("disabled");
      this.nextButton?.setAttribute("disabled", "");
    }

    this.update();
  }

  goTo(index) {
    this.activeIndex = index;
    this.update();
  }

  update() {
    const total = this.slides.length;

    this.updateTrackSize();
    this.updateButtonState();

    this.slides.forEach((slide, index) => {
      let offset = index - this.activeIndex;

      /*
       * Convert offset để slider loop
       *
       * Example:
       * active = 0
       *
       * slide 4 => -1
       * slide 1 => +1
       */
      if (this.loop) {
        if (offset > total / 2) {
          offset -= total;
        }

        if (offset < -total / 2) {
          offset += total;
        }
      }

      const absOffset = Math.abs(offset);

      let x = 0;
      let scale = 1;
      let rotate = 0;
      let opacity = 1;
      let zIndex = 10;

      // Active
      if (offset === 0) {
        x = 0;
        scale = 1;
        rotate = 0;
        opacity = 1;
        zIndex = 10;
      }

      // Left
      else if (offset === -1) {
        x = -220;
        scale = 0.8;
        rotate = 0;
        opacity = 0.5;
        zIndex = 5;
      }

      // Right
      else if (offset === 1) {
        x = 220;
        scale = 0.8;
        rotate = 0;
        opacity = 0.5;
        zIndex = 5;
      }

      // Các card phía sau
      else {
        x = offset < 0 ? -300 : 300;
        scale = 0.6;
        rotate = offset < 0 ? 35 : -35;
        opacity = 0;
        zIndex = 1;
      }

      slide.style.setProperty("--offset", offset);
      slide.style.setProperty("--x", `${x}px`);
      slide.style.setProperty("--scale", scale);
      slide.style.setProperty("--rotate", `${rotate}deg`);
      slide.style.setProperty("--opacity", opacity);
      slide.style.setProperty("--z-index", zIndex);

      slide.classList.toggle(
        "is-active",
        offset === 0
      );
    });

    this.setAttribute(
      "data-active-index",
      this.activeIndex
    );
  }

  updateTrackSize() {
    const activeSlide = this.slides[this.activeIndex];

    if (!activeSlide) return;

    const width = activeSlide.offsetWidth;
    const height = activeSlide.offsetHeight;

    this.style.setProperty("--track-width", `${width}px`);
    this.style.setProperty("--track-height", `${height}px`);
  }

  updateButtonState() {
    if (this.loop) {
      this.prevButton?.removeAttribute("disabled");
      this.nextButton?.removeAttribute("disabled");
      return;
    }

    this.prevButton?.toggleAttribute(
      "disabled",
      this.activeIndex === 0
    );

    this.nextButton?.toggleAttribute(
      "disabled",
      this.activeIndex === this.slides.length - 1
    );
  }
}

if (!customElements.get("coverflow-slider")) {
  customElements.define("coverflow-slider", CoverflowSlider);
}

class ArcSlider extends HTMLElement {
  constructor() {
    super();

    this.position = 0;
    this.targetPosition = 0;

    this.isAnimating = false;
    this.isDragging = false;

    this.dragStartX = 0;
    this.dragStartPosition = 0;

    this.raf = null;

    this.animationStart = 0;
    this.animationFrom = 0;
    this.animationTo = 0;

    this.animationDuration = 650;
    this.loop = this.getAttribute('loop') !== 'false';
  }

  connectedCallback() {
    this.viewport = this.querySelector('.arc-slider__viewport');
    this.track = this.querySelector('.arc-slider__track');
    this.items = [
      ...this.querySelectorAll('.arc-slider__item')
    ];

    this.prevButton = this.querySelector('.arc-slider__button--prev');
    this.nextButton = this.querySelector('.arc-slider__button--next');

    if (!this.items.length) return;

    this.setup();
    this.bindEvents();
    this.update();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.raf);
    this.resizeObserver?.disconnect();
    
    window.removeEventListener(
      'mousemove',
      this.onDragMove
    );

    window.removeEventListener(
      'mouseup',
      this.onDragEnd
    );

    window.removeEventListener(
      'touchmove',
      this.onDragMove
    );

    window.removeEventListener(
      'touchend',
      this.onDragEnd
    );
  }

  /* ========================================
     SETUP
  ======================================== */

  setup() {
    this.resizeObserver =
      new ResizeObserver(() => {
        this.update();
      });

    this.resizeObserver.observe(
      this.viewport
    );
  }

  /* ========================================
     DIMENSIONS
  ======================================== */

  get cardWidth() {
    return this.items[0].offsetWidth;
  }


  get gap() {
    const style = getComputedStyle(this);

    return parseFloat(
      style.getPropertyValue('--card-gap')
    ) || 20;
  }

  /*
   * Radius of the circle.
   *
   * Bigger = flatter arc
   * Smaller = stronger arc
   */

  get radius() {
    return Math.max(
      700,
      this.viewport.offsetWidth * 0.75
    );
  }

  /*
   * Distance between two cards
   */

  get angleStep() {
    const distance = this.cardWidth + this.gap;
    const radius =  this.radius;

    return 2 * Math.asin(
      Math.min(
        0.99,
        distance / (2 * radius)
      )
    );
  }

  /* ========================================
     LOOP HELPERS
  ======================================== */

  /*
   * Convert any number to:
   *
   * 0 ... length - 1
   */

  wrap(index) {
    const length = this.items.length;

    return (
      ((index % length) + length) %
      length
    );
  }


  /*
   * Get shortest relative distance
   * between an item and current position.
   *
   * Example with 5 slides:
   *
   * position = 0
   *
   * index 0 →  0
   * index 1 →  1
   * index 2 →  2
   * index 3 → -2
   * index 4 → -1
   *
   * Therefore slide 4 appears immediately
   * on the left of slide 0.
   */

  getLoopedRelative(index) {
    const length =
      this.items.length;

    let relative =
      index - this.position;

    relative =
      (
        relative +
        length / 2
      ) % length;

    if (relative < 0) {
      relative += length;
    }

    relative -= length / 2;

    return relative;
  }


  /* ========================================
     CIRCLE POSITION
  ======================================== */

  getCardTransform(index) {
    /*
     * IMPORTANT:
     *
     * Use shortest distance on the loop.
     */

    const relative = this.loop ? this.getLoopedRelative(index) : index - this.position;


    /*
     * Convert index → angle.
     */

    const theta =
      relative *
      this.angleStep;


    /*
     * Circle equation
     */

    const x =
      this.radius *
      Math.sin(theta);

    let y = this.radius * 1.2 *(1 - Math.cos(theta));


    /*
     * Tangent rotation
     */

    let rotation =
      theta * 180 / Math.PI * 1.3;

    const distance = Math.min(Math.abs(relative), 1);
    const centerProgress = 1 - distance;

    y += -10 * centerProgress;
    rotation += 2 * centerProgress;

    return {
      x,
      y,
      rotation
    };
  }


  /* ========================================
     UPDATE SLIDES
  ======================================== */

  update() {
    this.items.forEach(
      (item, index) => {

        const {
          x,
          y,
          rotation
        } =
          this.getCardTransform(index);


        item.style.transform =
          `
            translate3d(
              ${x}px,
              ${y}px,
              0
            )
            rotate(${rotation}deg)
          `;


        /*
         * Active slide
         *
         * Because position can be:
         *
         * 0
         * 1
         * 2
         * 3.5
         * -1
         *
         * We calculate the looped distance.
         */

        const distance =
          Math.abs(
            this.getLoopedRelative(index)
          );


        item.classList.toggle(
          'is-active',
          distance < 0.5
        );
      }
    );


    this.updateButtons();
  }


  /* ========================================
     NAVIGATION
  ======================================== */

  next() {
    if (this.isAnimating) {
      return;
    }

    if (this.loop) {
      this.animateTo(
        Math.round(this.position) + 1
      );

      return;
    }

    this.animateTo(
      Math.round(this.position) + 1
    );
  }


  prev() {
    if (this.isAnimating) {
      return;
    }

    if (this.loop) {

      this.animateTo(
        Math.round(this.position) - 1
      );

      return;
    }

    this.animateTo(
      Math.round(this.position) - 1
    );
  }


  animateTo(target) {
    /*
     * Không clamp target nữa.
     *
     * Cho phép:
     *
     * 0 → 1
     * 1 → 2
     * 4 → 5
     * 5 → 6
     *
     * hoặc:
     *
     * 0 → -1
     * -1 → -2
     */

    if (!this.loop) {

      target =
        Math.max(
          0,
          Math.min(
            this.items.length - 1,
            target
          )
        );
    }

    this.animationFrom =
      this.position;

    this.animationTo =
      target;

    this.animationStart =
      performance.now();

    this.isAnimating = true;


    cancelAnimationFrame(
      this.raf
    );


    this.raf =
      requestAnimationFrame(
        this.animate.bind(this)
      );
  }


  animate(timestamp) {
    if (!this.isAnimating) {
      return;
    }


    const elapsed =
      timestamp -
      this.animationStart;


    let progress =
      Math.min(
        elapsed /
        this.animationDuration,
        1
      );


    /*
     * Ease out quartic
     */

    progress =
      1 -
      Math.pow(
        1 - progress,
        4
      );


    this.position =
      this.animationFrom +
      (
        this.animationTo -
        this.animationFrom
      ) *
      progress;


    this.update();


    if (progress < 1) {

      this.raf =
        requestAnimationFrame(
          this.animate.bind(this)
        );

    } else {

      this.position =
        this.animationTo;

      this.isAnimating = false;

      this.update();
    }
  }

  /* ========================================
     DRAG
  ======================================== */

  bindEvents() {
    this.prevButton?.addEventListener(
      'click',
      () => this.prev()
    );

    this.nextButton?.addEventListener(
      'click',
      () => this.next()
    );


    /*
     * Mouse
     */

    this.viewport.addEventListener(
      'mousedown',
      this.onDragStart.bind(this)
    );

    this.onDragMove =
      this.onDragMove.bind(this);

    this.onDragEnd =
      this.onDragEnd.bind(this);

    window.addEventListener(
      'mousemove',
      this.onDragMove
    );

    window.addEventListener(
      'mouseup',
      this.onDragEnd
    );


    /*
     * Touch
     */

    this.viewport.addEventListener(
      'touchstart',
      this.onDragStart.bind(this),
      {
        passive: true
      }
    );

    this.onDragMoveTouch =
      this.onDragMove.bind(this);

    window.addEventListener(
      'touchmove',
      this.onDragMoveTouch,
      {
        passive: true
      }
    );

    window.addEventListener(
      'touchend',
      this.onDragEnd
    );
  }


  getPointerX(event) {
    if (event.touches) {

      return event.touches[0].clientX;
    }

    return event.clientX;
  }


  onDragStart(event) {
    if (
      event.type === 'mousedown' &&
      event.button !== 0
    ) {
      return;
    }


    /*
     * Stop animation
     */

    cancelAnimationFrame(
      this.raf
    );

    this.isAnimating = false;


    this.isDragging = true;


    this.dragStartX =
      this.getPointerX(event);

    this.dragStartPosition =
      this.position;


    this.viewport.classList.add(
      'is-dragging'
    );
  }

  onDragMove(event) {
    if (!this.isDragging) {
      return;
    }

    const currentX =
      this.getPointerX(event);


    const delta =
      currentX -
      this.dragStartX;

    let position =
      this.dragStartPosition -
      delta /
      (this.cardWidth + this.gap);

    /*
     * Convert pixels → slide position.
     *
     * 1 card width = 1 slide.
     *
     * NO EDGE RESISTANCE.
     *
     * Because this is an infinite loop.
     */

    if (!this.loop) {

      if (position < 0) {

        position =
          position * 0.25;
      }


      if (
        position >
        this.items.length - 1
      ) {

        const max =
          this.items.length - 1;

        position =
          max +
          (position - max) * 0.25;
      }
    }

    this.position = position;

    this.update();
  }

  onDragEnd() {
    if (!this.isDragging) {
      return;
    }


    this.isDragging = false;


    this.viewport.classList.remove(
      'is-dragging'
    );


    /*
     * Snap to nearest slide.
     */

    const target =
      Math.round(this.position);

    if (!this.loop) {

      target =
        Math.max(
          0,
          Math.min(
            this.items.length - 1,
            target
          )
        );
    }
    this.animateTo(target);
  }

  /* ========================================
     BUTTONS
  ======================================== */

  updateButtons() {
    if (this.loop) {

      if (this.prevButton) {
        this.prevButton.disabled = false;
      }

      if (this.nextButton) {
        this.nextButton.disabled = false;
      }

      return;
    }

    if (this.prevButton) {

      this.prevButton.disabled =
        this.position <= 0;
    }

    if (this.nextButton) {

      this.nextButton.disabled =
        this.position >=
        this.items.length - 1;
    }
  }
}

if (!customElements.get("arc-slider")) {
  customElements.define('arc-slider', ArcSlider);
}